import { useState } from "react";
import { Check, Copy, QrCode, ArrowSquareOut, Sparkle, Upload } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Card, Field, Grid, ReadOnlyNote, inputCls, btnPrimary } from "./shared";

const TZ = ["Europe/Rome", "Europe/London", "Europe/Paris", "Europe/Berlin", "America/New_York"];
const VALUTE = ["EUR", "CHF", "GBP", "USD"];
const LINGUE = [["it", "Italiano"], ["en", "English"], ["fr", "Français"], ["de", "Deutsch"], ["es", "Español"]];

export default function GeneraliSection({ settings, onSave, saving, readOnly }) {
  const [g, setG] = useState(settings.generali || {});
  const [m, setM] = useState(settings.marketplace || {});
  const [name, setName] = useState(settings.restaurant_name || "");
  const [autoOut, setAutoOut] = useState(settings.auto_out_minutes ?? 90);
  const [slug, setSlug] = useState(settings.public_slug || "");
  const [copied, setCopied] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);
  const set = (k, v) => setG({ ...g, [k]: v });
  const setMk = (k, v) => setM({ ...m, [k]: v });
  const [aiUrl, setAiUrl] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [upBusy, setUpBusy] = useState(false);

  const aiFill = async () => {
    if (!aiUrl.trim()) { toast.error("Inserisci l'indirizzo del sito"); return; }
    setAiBusy(true);
    try {
      const r = await api.post("/settings/marketplace/ai-fill", { sito_url: aiUrl });
      const mk = Object.fromEntries(Object.entries(r.data.marketplace || {}).filter(([, v]) => v));
      const gen = Object.fromEntries(Object.entries(r.data.generali || {}).filter(([, v]) => v));
      setM({ ...m, ...mk });
      setG({ ...g, ...gen });
      toast.success("Profilo compilato dall'IA: verifica i campi e poi salva");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lettura AI non riuscita");
    } finally { setAiBusy(false); }
  };

  const uploadFoto = async (e, target = "cover") => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (target === "gallery" && (m.galleria || []).length >= 8) {
      toast.error("Massimo 8 foto in galleria");
      e.target.value = "";
      return;
    }
    setUpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/marketing/uploads/image", fd);
      if (target === "gallery") setM({ ...m, galleria: [...(m.galleria || []), r.data.url] });
      else setMk("foto_url", r.data.url);
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload non riuscito");
    } finally { setUpBusy(false); e.target.value = ""; }
  };
  const bookingUrl = `${window.location.origin}/p/${slug.trim() || settings.restaurant_id}`;

  const copyLink = () => {
    navigator.clipboard.writeText(bookingUrl).then(() => {
      setCopied(true);
      toast.success("Link pubblico copiato");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const showQr = async () => {
    try {
      const r = await api.get("/tools/qr", { params: { content: bookingUrl }, responseType: "blob" });
      setQrUrl(URL.createObjectURL(r.data));
    } catch { toast.error("QR non disponibile"); }
  };

  return (
    <div className="space-y-5" data-testid="setsection-generali">
      <Card title="Informazioni struttura" desc="Dati operativi visibili ai clienti e usati nelle comunicazioni." testid="gen-info">
        <Grid>
          <Field label="Nome ristorante">
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="gen-restaurant-name" />
          </Field>
          <Field label="Nome gestore">
            <input value={g.nome_gestore || ""} onChange={(e) => set("nome_gestore", e.target.value)} className={inputCls} data-testid="gen-gestore" />
          </Field>
          <Field label="Sito web">
            <input value={g.sito_web || ""} onChange={(e) => set("sito_web", e.target.value)} placeholder="https://…" className={inputCls} data-testid="gen-sito" />
          </Field>
          <Field label="Dominio / frontpage del ristorante">
            <input value={g.dominio || ""} onChange={(e) => set("dominio", e.target.value)} placeholder="prenota.ristorante.it" className={inputCls} data-testid="gen-dominio" />
          </Field>
        </Grid>
      </Card>

      <Card title="Contatti e comunicazioni" desc="Recapiti usati come mittente e riferimento nei messaggi ai clienti." testid="gen-contatti">
        <Grid>
          <Field label="Telefono della struttura">
            <input value={g.telefono || ""} onChange={(e) => set("telefono", e.target.value)} className={inputCls} data-testid="gen-telefono" />
          </Field>
          <Field label="Numero WhatsApp">
            <input value={g.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} data-testid="gen-whatsapp" />
          </Field>
          <Field label="Indirizzo email">
            <input type="email" value={g.email_contatto || ""} onChange={(e) => set("email_contatto", e.target.value)} className={inputCls} data-testid="gen-email" />
          </Field>
          <Field label="Mittente SMS">
            <input value={g.mittente_sms || ""} onChange={(e) => set("mittente_sms", e.target.value)} maxLength={11} placeholder="Max 11 caratteri" className={inputCls} data-testid="gen-mittente-sms" />
          </Field>
        </Grid>
      </Card>

      <Card title="Ubicazione e operatività" testid="gen-ubicazione">
        <Grid>
          <Field label="Indirizzo attività">
            <input value={g.indirizzo || ""} onChange={(e) => set("indirizzo", e.target.value)} className={inputCls} data-testid="gen-indirizzo" />
          </Field>
          <Field label="Città">
            <input value={g.citta || ""} onChange={(e) => set("citta", e.target.value)} className={inputCls} data-testid="gen-citta" />
          </Field>
          <Field label="CAP">
            <input value={g.cap || ""} onChange={(e) => set("cap", e.target.value)} className={inputCls} data-testid="gen-cap" />
          </Field>
          <Field label="Provincia">
            <input value={g.provincia || ""} onChange={(e) => set("provincia", e.target.value)} className={inputCls} data-testid="gen-provincia" />
          </Field>
          <Field label="Paese">
            <input value={g.paese || ""} onChange={(e) => set("paese", e.target.value)} className={inputCls} data-testid="gen-paese" />
          </Field>
          <Field label="Timezone">
            <select value={g.timezone || "Europe/Rome"} onChange={(e) => set("timezone", e.target.value)} className={inputCls} data-testid="gen-timezone">
              {TZ.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Valuta">
            <select value={g.valuta || "EUR"} onChange={(e) => set("valuta", e.target.value)} className={inputCls} data-testid="gen-valuta">
              {VALUTE.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Lingua front-end pubblico">
            <select value={g.lingua || "it"} onChange={(e) => set("lingua", e.target.value)} className={inputCls} data-testid="gen-lingua">
              {LINGUE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
        </Grid>
        <Field label="OUT automatico dopo (minuti dall'arrivo)">
          <input type="number" min="15" step="5" value={autoOut} onChange={(e) => setAutoOut(e.target.value)} className={inputCls} data-testid="set-auto-out" />
          <p className="text-xs text-zinc-500 mt-1">Il cliente passa automaticamente da IN a OUT dopo questo tempo. L'OUT manuale resta sempre disponibile.</p>
        </Field>
      </Card>

      <Card title="Recensioni" desc="Link usati nelle comunicazioni post-visita per invitare alla recensione." testid="gen-recensioni">
        <Grid>
          <Field label="Google Review URL">
            <input value={g.google_review_url || ""} onChange={(e) => set("google_review_url", e.target.value)} placeholder="https://g.page/…" className={inputCls} data-testid="gen-google-url" />
          </Field>
          <Field label="TripAdvisor Review URL">
            <input value={g.tripadvisor_url || ""} onChange={(e) => set("tripadvisor_url", e.target.value)} placeholder="https://tripadvisor.it/…" className={inputCls} data-testid="gen-tripadvisor-url" />
          </Field>
        </Grid>
      </Card>

      <Card title="Vetrina pubblica Dishenda (portale Prenota)"
        desc="Mostra il tuo locale nel portale pubblico di Dishenda: i clienti ti trovano su Google e prenotano direttamente, senza commissioni."
        testid="gen-marketplace">
        <label className="flex items-center gap-3 text-sm font-semibold text-zinc-800" data-testid="gen-mk-attiva-wrap">
          <input type="checkbox" checked={!!m.attiva} onChange={(e) => setMk("attiva", e.target.checked)}
            className="h-4 w-4" style={{ accentColor: "#0e7490" }} data-testid="gen-mk-attiva" />
          Appari nella vetrina pubblica (opt-in)
        </label>
        {m.attiva && (
          <>
            <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3 flex flex-wrap items-center gap-2" data-testid="gen-mk-ai">
              <input value={aiUrl} onChange={(e) => setAiUrl(e.target.value)} placeholder="www.ilsitodellocale.it"
                className={`${inputCls} flex-1 min-w-48`} data-testid="gen-mk-ai-url" />
              <button type="button" onClick={aiFill} disabled={aiBusy} data-testid="gen-mk-ai-fill"
                className="h-11 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                <Sparkle size={14} weight="fill" /> {aiBusy ? "Lettura del sito…" : "Compila con IA"}
              </button>
              <p className="w-full text-[11px] text-zinc-500">L'IA legge il sito del locale e pre-compila descrizione, tipologia, fascia prezzo, indirizzo e foto. Nulla viene salvato finché non clicchi "Salva Generali".</p>
            </div>
            <Grid>
              <Field label="Tipologia di locale">
                <select value={m.tipologia || ""} onChange={(e) => setMk("tipologia", e.target.value)} className={inputCls} data-testid="gen-mk-tipologia">
                  <option value="">— scegli —</option>
                  {["Ristorante", "Pizzeria", "Trattoria", "Osteria", "Sushi", "Bistrò", "Hamburgeria", "Pub", "Birreria", "Cocktail Bar", "Enoteca", "Agriturismo"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Fascia di prezzo">
                <select value={m.fascia_prezzo || ""} onChange={(e) => setMk("fascia_prezzo", e.target.value)} className={inputCls} data-testid="gen-mk-fascia">
                  <option value="">—</option>
                  <option value="€">€ economico</option>
                  <option value="€€">€€ medio</option>
                  <option value="€€€">€€€ alto</option>
                </select>
              </Field>
              <Field label="Foto copertina (card del portale)">
                <input value={m.foto_url || ""} onChange={(e) => setMk("foto_url", e.target.value)} placeholder="https://… oppure carica un file" className={inputCls} data-testid="gen-mk-foto" />
                <div className="flex items-center gap-2 mt-1.5">
                  <label className="h-8 px-3 rounded-md border border-zinc-300 text-[11px] font-semibold flex items-center gap-1.5 cursor-pointer hover:border-zinc-500 transition-colors" data-testid="gen-mk-foto-upload">
                    <Upload size={13} /> {upBusy ? "Caricamento…" : "Carica foto"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadFoto(e, "cover")} />
                  </label>
                  {m.foto_url && <img src={m.foto_url} alt="Anteprima foto vetrina" className="h-8 w-12 object-cover rounded" data-testid="gen-mk-foto-preview" />}
                </div>
              </Field>
            </Grid>
            <Field label="Galleria foto (carosello nella tua pagina pubblica)">
              <div className="flex flex-wrap gap-2" data-testid="gen-mk-gallery">
                {(m.galleria || []).map((u, i) => (
                  <div key={i} className="relative group">
                    <img src={u} alt={`Foto vetrina ${i + 1}`} className="h-16 w-24 object-cover rounded-md border border-zinc-200" />
                    <button type="button" onClick={() => setMk("galleria", (m.galleria || []).filter((_, j) => j !== i))}
                      data-testid={`gen-mk-gal-del-${i}`}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-zinc-900 text-white text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                  </div>
                ))}
                {(m.galleria || []).length < 8 && (
                  <label className="h-16 w-24 rounded-md border border-dashed border-zinc-300 text-[10px] font-semibold text-zinc-500 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-zinc-500 transition-colors" data-testid="gen-mk-gal-add">
                    <Upload size={14} /> {upBusy ? "…" : "Aggiungi"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadFoto(e, "gallery")} />
                  </label>
                )}
              </div>
              <p className="text-[11px] text-zinc-500 mt-1.5">Sala, piatti, terrazza: le foto scorrono a carosello nella tua pagina sul portale. Max 8.</p>
            </Field>
            <Field label="Descrizione pubblica">
              <textarea value={m.descrizione || ""} onChange={(e) => setMk("descrizione", e.target.value)} rows={3}
                placeholder="Racconta il tuo locale in due righe: cucina, atmosfera, punti forti…"
                className={`${inputCls} h-auto py-3 resize-none`} data-testid="gen-mk-desc" />
              <p className="text-xs text-zinc-500 mt-1">Zona, telefono, orari, menu ed eventi vengono presi in automatico dalle impostazioni, dal menu digitale e dal calendario eventi.</p>
            </Field>
          </>
        )}
      </Card>

      <Card title="Modulo di prenotazione pubblico"
        desc="Questo è il link che vedono i clienti per prenotare online: condividilo su Google, social, WhatsApp o stampa il QR per il tavolo."
        testid="booking-link-card">
        <Field label="Link breve personalizzato (slug)">
          <div className="flex items-center gap-0">
            <span className="h-11 px-3 rounded-l-md border border-r-0 border-zinc-300 bg-zinc-50 text-sm text-zinc-500 flex items-center font-mono">/p/</span>
            <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              placeholder="ninfa-restaurant" maxLength={40}
              className={`${inputCls} rounded-l-none`} data-testid="booking-slug" />
          </div>
          <p className="text-xs text-zinc-500 mt-1">3-40 caratteri: lettere minuscole, numeri e trattini. Se lasci vuoto si usa l'indirizzo tecnico lungo. Salva con "Salva Generali" per attivarlo.</p>
        </Field>
        <div className="flex items-center gap-2 flex-wrap">
          <input readOnly value={bookingUrl} onFocus={(e) => e.target.select()}
            className={`${inputCls} flex-1 min-w-60 font-mono text-xs`} data-testid="booking-link-url" />
          <button type="button" onClick={copyLink} data-testid="booking-link-copy"
            className="h-11 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
            {copied ? <Check size={14} weight="bold" /> : <Copy size={14} />} {copied ? "Copiato" : "Copia link"}
          </button>
          <a href={bookingUrl} target="_blank" rel="noreferrer" data-testid="booking-link-open"
            className="h-11 px-4 rounded-md border border-zinc-300 text-xs font-semibold flex items-center gap-1.5">
            <ArrowSquareOut size={14} /> Apri
          </a>
          <button type="button" onClick={showQr} data-testid="booking-link-qr"
            className="h-11 px-4 rounded-md border border-zinc-300 text-xs font-semibold flex items-center gap-1.5">
            <QrCode size={14} /> QR
          </button>
        </div>
        {qrUrl && (
          <div className="flex items-center gap-4 pt-2">
            <img src={qrUrl} alt="QR prenotazioni" className="w-32 h-32 border border-zinc-200 rounded-lg" data-testid="booking-link-qr-img" />
            <p className="text-xs text-zinc-500 max-w-56">Scaricalo da Strumenti → Generatore QR Code per stamparlo in alta risoluzione.</p>
          </div>
        )}
        <p className="text-xs text-zinc-500">Le landing degli <b>eventi</b> (data bloccata) si copiano dalla pagina Eventi.</p>
      </Card>

      {readOnly ? <ReadOnlyNote /> : (
        <button disabled={saving} onClick={() => onSave({ restaurant_name: name, auto_out_minutes: parseInt(autoOut) || 90, public_slug: slug.trim(), generali: g, marketplace: m })}
          className={btnPrimary} data-testid="gen-save">Salva Generali</button>
      )}
    </div>
  );
}
